import React, { useState } from 'react';
import { bookingAPI } from '../services/api';

const TicketVerification = () => {
  const [verificationMethod, setVerificationMethod] = useState('pnr'); // 'pnr' or 'qr'
  const [pnr, setPnr] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [verificationResult, setVerificationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setVerificationResult(null);

    try {
      let result;
      
      if (verificationMethod === 'pnr' && pnr) {
        result = await bookingAPI.verifyTicketByPNR(pnr);
      } else if (verificationMethod === 'qr' && qrCode) {
        result = await bookingAPI.verifyTicketByQR(qrCode);
      } else {
        throw new Error('Please provide the required information');
      }
      
      setVerificationResult(result);
    } catch (err) {
      setError(err.message || 'Failed to verify ticket. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status.toLowerCase()) {
      case 'confirmed':
        return 'badge badge-success';
      case 'cancelled':
        return 'badge badge-error';
      case 'pending':
        return 'badge badge-warning';
      default:
        return 'badge badge-info';
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[var(--foreground)] mb-2">Ticket Verification</h1>
        <p className="text-[var(--gray)]">Verify ticket validity using PNR number or QR code</p>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6 card">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-[var(--foreground)] mb-4">Verification Method</h2>
          <div className="flex space-x-4">
            <button
              onClick={() => setVerificationMethod('pnr')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                verificationMethod === 'pnr'
                  ? 'bg-[var(--primary)] text-white'
                  : 'bg-[var(--light)] text-[var(--foreground)] hover:bg-[var(--primary-hover)]'
              }`}
            >
              Verify by PNR
            </button>
            <button
              onClick={() => setVerificationMethod('qr')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                verificationMethod === 'qr'
                  ? 'bg-[var(--primary)] text-white'
                  : 'bg-[var(--light)] text-[var(--foreground)] hover:bg-[var(--background)]'
              }`}
            >
              Verify by QR Code
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleVerify}>
          {verificationMethod === 'pnr' ? (
            <div className="mb-6">
              <label className="block text-sm font-medium text-[var(--gray)] mb-2">
                PNR Number
              </label>
              <input
                type="text"
                value={pnr}
                onChange={(e) => setPnr(e.target.value.toUpperCase())}
                placeholder="Enter 6-character PNR number"
                className="w-full px-4 py-2 bg-[var(--background)] text-[var(--foreground)] border border-[var(--border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:border-[var(--primary)]"
                maxLength="8"
                required
              />
              <p className="mt-2 text-sm text-[var(--gray)]">
                Enter the 6-character PNR number from your booking confirmation
              </p>
            </div>
          ) : (
            <div className="mb-6">
              <label className="block text-sm font-medium text-[var(--gray)] mb-2">
                QR Code
              </label>
              <input
                type="text"
                value={qrCode}
                onChange={(e) => setQrCode(e.target.value)}
                placeholder="Scan or enter QR code data"
                className="w-full px-4 py-2 bg-[var(--background)] text-[var(--foreground)] border border-[var(--border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:border-[var(--primary)]"
                required
              />
              <div className="mt-4 flex justify-center">
                <div className="bg-[var(--light)] p-4 rounded-lg border border-[var(--border)]">
                  <div className="w-32 h-32 flex items-center justify-center border-2 border-dashed border-[var(--border)]">
                    <span className="text-[var(--gray)] text-xs text-center">
                      QR Code Scanner<br />
                      (Placeholder)
                    </span>
                  </div>
                </div>
              </div>
              <p className="mt-2 text-sm text-[var(--gray)]">
                Scan the QR code on the ticket or enter the QR code data manually
              </p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary hover:shadow-lg transform hover:scale-105 transition duration-300 w-full sm:w-auto"
          >
            {loading ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Verifying...
              </span>
            ) : (
              'Verify Ticket'
            )}
          </button>
        </form>
      </div>

      {verificationResult && (
        <div className="mt-8 bg-white rounded-xl shadow-md p-6 card">
          <h2 className="text-xl font-semibold text-[var(--foreground)] mb-4">Verification Result</h2>
          
          {verificationResult.isValid ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <svg className="h-8 w-8 text-[rgb(var(--color-primary-green)/<alpha-value>)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-[rgb(var(--color-primary-green)/<alpha-value>)]">Valid Ticket</h3>
                  <p className="text-sm text-[rgb(var(--color-primary-green)/<alpha-value>)]">{verificationResult.message}</p>
                </div>
              </div>
              
              <div className="border-t border-green-200 pt-4">
                <h4 className="font-medium text-[rgb(var(--color-primary-green)/<alpha-value>)] mb-3">Booking Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-[rgb(var(--color-primary-green)/<alpha-value>)]">PNR Number</p>
                    <p className="font-medium text-green-800">{verificationResult.booking.pnr}</p>
                  </div>
                  <div>
                    <p className="text-sm text-[rgb(var(--color-primary-green)/<alpha-value>)]">Status</p>
                    <span className={getStatusBadgeClass(verificationResult.booking.status)}>
                      {verificationResult.booking.status}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-[rgb(var(--color-primary-green)/<alpha-value>)]">Travel Date</p>
                    <p className="font-medium text-green-800">
                      {new Date(verificationResult.booking.travelDate).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-[rgb(var(--color-primary-green)/<alpha-value>)]">Total Amount</p>
                    <p className="font-medium text-green-800">৳{verificationResult.booking.totalAmount.toFixed(2)}</p>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-green-200">
                  <h5 className="font-medium text-[rgb(var(--color-primary-green)/<alpha-value>)] mb-2">Bus Information</h5>
                  <p className="text-[rgb(var(--color-primary-green)/<alpha-value>)]">
                    {verificationResult.booking.bus.busNumber} ({verificationResult.booking.bus.busType})
                  </p>
                  <p className="text-green-700">
                    {verificationResult.booking.bus.route.origin} → {verificationResult.booking.bus.route.destination}
                  </p>
                  <p className="text-green-700">
                    Departure: {verificationResult.booking.bus.departureTime} |
                    Arrival: {verificationResult.booking.bus.arrivalTime}
                  </p>
                  <p className="text-green-700">Boarding Point: {verificationResult.booking.bus.route.origin}</p>
                  <p className="text-green-700">Dropping Off Point: {verificationResult.booking.bus.route.destination}</p>
                </div>
                
                <div className="mt-4 pt-4 border-t border-green-200">
                  <h5 className="font-medium text-[rgb(var(--color-primary-green)/<alpha-value>)] mb-2">Passengers</h5>
                  {verificationResult.booking.passengers.map((passenger, index) => (
                    <div key={index} className="mb-2 last:mb-0">
                      <p className="font-medium text-green-800">{passenger.name}</p>
                      <p className="text-sm text-[rgb(var(--color-primary-green)/<alpha-value>)]">
                        Age: {passenger.age} | Gender: {passenger.gender}
                      </p>
                      <p className="text-sm text-[rgb(var(--color-primary-green)/<alpha-value>)]">
                        Phone: {passenger.phone}
                      </p>
                    </div>
                  ))}
                </div>
                
                <div className="mt-4 pt-4 border-t border-green-200">
                  <h5 className="font-medium text-[rgb(var(--color-primary-green)/<alpha-value>)] mb-2">Selected Seats</h5>
                  <div className="flex flex-wrap gap-2">
                    {verificationResult.booking.selectedSeats.map((seat, index) => (
                      <span key={index} className="bg-green-100 text-[rgb(var(--color-primary-green)/<alpha-value>)] px-3 py-1 rounded-full text-sm">
                        {seat.number} ({seat.type})
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-8 w-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-red-800">Invalid Ticket</h3>
                  <p className="text-sm text-red-700">{verificationResult.message}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TicketVerification;